import { useMutation, useQuery } from '@apollo/client';
import { confirm } from '@components/ConfirmModal/confirm';
import { DEFAULT_PAGE_SIZE } from '@components/Pagination';
import { useSnackbar } from '@components/Snackbar/SnackbarProvider';
import {
    DELETE_ORGANIZATION_USER_PRO,
    GET_DETAIL_ORGANIZATION_USER_PRO,
    RESEND_INVITATION_ORGANIZATION_USER_PRO,
    UPDATE_ORGANIZATION_USER_PRO,
} from '@gql/organization-users';
import { GET_PRO } from '@gql/pros';
import { generatePassword } from '@helper/string.utils';
import FeatureCreditsDisabler from '@modules/features/FeatureCreditsDisabler';
import { useGetOrganizationUsers } from '@modules/organizationUsers/hooks';
import { Box, Button, Stack, styled } from '@mui/material';
import { FeatureNames } from '@utils/permissions';
import { Roles } from '@utils/roleUtils';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { SelectionModel } from '@components/DataTable/types';
import OrganizationUsersDataGrid from './containers/OrganizationUsersDataGrid';
import OrganizationUsersDataTable from './containers/OrganizationUsersDataTable';
import OrganizationUserFilter, {
    defaultOrganizationUserFilter,
    IOrganizationUserFilter,
    IOrganizationUserSort,
    ViewType,
} from './OrganizationUserFilter';

// ─── Component ────────────────────────────────────────────────────────────────

const OrganizationUserList = () => {
    const { id: companyId, userId } = useParams<{ id: string; userId: string }>();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();

    // ── Pro data ──────────────────────────────────────────────────────────────

    const { data: proData, loading: loadingProData } = useQuery(GET_PRO, {
        variables: { proId: companyId },
        skip: !companyId,
    });
    const pro = proData?.getPro;

    // ── Higherup user (for sub-team view) ────────────────────────────────────

    const { data: dataUser } = useQuery(GET_DETAIL_ORGANIZATION_USER_PRO, {
        variables: { id: userId, proId: companyId },
        skip: !userId,
    });

    // ── View & filter state ───────────────────────────────────────────────────

    const [viewType, setViewType] = useState<ViewType>('grid');

    const [matcher, setMatcher] = useState<{
        query: string;
        sort: IOrganizationUserSort;
        pagination: { offset: number; limit: number };
        filter: IOrganizationUserFilter;
    }>({
        query: '',
        sort: { field: 'name', order: 'asc' },
        pagination: { offset: 0, limit: DEFAULT_PAGE_SIZE },
        filter: defaultOrganizationUserFilter,
    });

    // Table-view pagination (1-indexed pages)
    const [tablePage, setTablePage] = useState(1);
    const [tablePageSize, setTablePageSize] = useState(DEFAULT_PAGE_SIZE);
    const [tableSelectionModel, setTableSelectionModel] = useState<SelectionModel>({ selectedRows: [], isAllSelected: false, isSomeSelected: false });

    // ── Data fetching ─────────────────────────────────────────────────────────

    const tablePagination = useMemo(() => ({
        offset: (tablePage - 1) * tablePageSize,
        limit: tablePageSize,
    }), [tablePage, tablePageSize]);

    const activePagination = viewType === 'list' ? tablePagination : matcher.pagination;

    const {
        organizationUsers,
        meta: hookMeta,
        loading: fetchLoading,
        refetch,
    } = useGetOrganizationUsers({
        query: matcher.query,
        proId: pro?._id,
        sort: matcher.sort,
        pagination: activePagination,
        higherUpId: userId || undefined,
        filter: matcher.filter,
        deep: false,
    });

    const meta = useMemo(() => ({
        totalDocs: hookMeta?.totalDocs ?? 0,
        totalPages: hookMeta?.totalPages ?? 0,
        hasNextPage: hookMeta?.hasNextPage ?? false,
        hasPrevPage: hookMeta?.hasPrevPage ?? false,
    }), [hookMeta]);

    // ── Mutations ─────────────────────────────────────────────────────────────

    const [updateOrganizationUser, queryUpdateState] = useMutation(UPDATE_ORGANIZATION_USER_PRO, {
        onCompleted: () => {
            enqueueSnackbar('Les données des membres ont été mises à jour avec succès', { variant: 'success' });
            refetch();
        },
        onError: err => enqueueSnackbar(err.message, { variant: 'error' }),
    });

    const [deleteOrganizationUser, deleteDataState] = useMutation(DELETE_ORGANIZATION_USER_PRO);

    const [resendInvitation, resendInvitationState] = useMutation(RESEND_INVITATION_ORGANIZATION_USER_PRO);

    const isActionLoading =
        queryUpdateState.loading || resendInvitationState.loading || deleteDataState.loading;

    // ── Handlers ──────────────────────────────────────────────────────────────

    const handleEdit = useCallback(
        (item: any) => {
            navigate(`/companies/${companyId}/users/${item._id}`);
        },
        [companyId, navigate],
    );

    const handleDelete = useCallback(
        async (id: string) => {
            if (!(await confirm({ confirmation: 'êtes-vous sûr de vouloir supprimer ces données?' }))) return;
            await deleteOrganizationUser({
                variables: { id, proId: pro?._id },
                updateQueries: {
                    getOrganizationUserWithPaginations: (prev, { mutationResult }) => {
                        const deletedId = mutationResult.data.deleteOrganizationUser._id;
                        return {
                            getOrganizationUserWithPaginations: {
                                ...prev.getOrganizationUserWithPaginations,
                                docs: prev.getOrganizationUserWithPaginations.docs
                                    .filter((item: any) => item._id !== deletedId)
                                    .map((item: any) => {
                                        const updated = { ...item };
                                        if (item.subordinates) {
                                            updated.subordinates = item.subordinates.filter(
                                                (s: any) => s._id !== deletedId,
                                            );
                                        }
                                        if (item.totalSubordinates > 0) {
                                            updated.totalSubordinates = item.totalSubordinates - 1;
                                        }
                                        return updated;
                                    }),
                            },
                        };
                    },
                },
                refetchQueries: [{ query: GET_PRO, variables: { proId: pro?._id } }],
                awaitRefetchQueries: true,
                onCompleted: () => refetch(),
            });
        },
        [deleteOrganizationUser, pro?._id, refetch],
    );

    const handleResendInvitation = useCallback(
        async (id: string) => {
            if (!(await confirm({ confirmation: 'êtes-vous sûr de vouloir renvoyer l`invitation ?' }))) return;
            await resendInvitation({
                variables: {
                    payload: { id, password: generatePassword() },
                    proId: pro?._id,
                },
            });
        },
        [pro?._id, resendInvitation],
    );

    const handleUpdateStatus = useCallback(
        async (item: any) => {
            const active = !item.isMemberActive;
            await updateOrganizationUser({
                variables: {
                    request: {
                        id: item._id,
                        email: item.email,
                        firstName: item.firstName ?? '',
                        lastName: item.lastName ?? '',
                        role: item.role,
                        isMemberActive: active,
                        active,
                    },
                    proId: pro?._id,
                },
            });
        },
        [pro?._id, updateOrganizationUser],
    );

    const handleAddMember = useCallback(() => {
        navigate(`/companies/${companyId}/users/create`);
    }, [companyId, navigate]);

    // ── Filter state updaters ─────────────────────────────────────────────────

    const handleQueryChange = useCallback((q: string) => {
        setMatcher(prev => ({ ...prev, query: q, pagination: { ...prev.pagination, offset: 0 } }));
        setTablePage(1);
    }, []);

    const handleSortChange = useCallback((sort: IOrganizationUserSort) => {
        setMatcher(prev => ({ ...prev, sort }));
    }, []);

    const handleFilterChange = useCallback((filter: IOrganizationUserFilter) => {
        setMatcher(prev => ({ ...prev, filter, pagination: { ...prev.pagination, offset: 0 } }));
        setTablePage(1);
    }, []);

    const handleGridPaginationChange = useCallback((pagination: { offset: number; limit: number }) => {
        setMatcher(prev => ({ ...prev, pagination }));
    }, []);

    // ── Guards ────────────────────────────────────────────────────────────────

    if (loadingProData) return <Box>Loading...</Box>;
    if (!pro) return <Box>Company not found</Box>;

    const canAddMember = dataUser?.getOrganizationUser?.role !== Roles.MEMBER_PRO;

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <Stack sx={{width: '100%'}}>
            <HeaderSection>
                <HeaderInfoGroup>
                    <Title>Membres de l'équipe</Title>
                    <Subtitle>
                        Gérez ici les membres de votre équipe et leurs droits d'accès au compte
                    </Subtitle>
                </HeaderInfoGroup>
                {canAddMember && (
                    <FeatureCreditsDisabler
                        proId={pro._id}
                        loading={isActionLoading}
                        roles={[Roles.ADMIN_PRO, Roles.COMPANY, Roles.UPERCUT]}
                        alert={{
                            title: 'Nombre maximum de profils Administrateur/Membre atteint',
                            message: (
                                <AlertTypography>
                                    Vos droit à la création d'un profil administrateur ou membre sont
                                    actuellement utilisés. Si vous souhaitez augmenter le nombre de profil
                                    administrateur, merci de nous contacter à l'adresse{' '}
                                    <a href="mailto:contact@upercut.fr">contact@upercut.fr</a>
                                </AlertTypography>
                            ),
                        }}
                        feature={[
                            FeatureNames.ADMIN_USERS_CREDITS,
                            FeatureNames.ACCOUNTANT_USERS_CREDITS,
                            FeatureNames.RECRUITER_USERS_CREDITS,
                        ]}>
                        <Button
                            color="primary"
                            startIcon={<i className="fi fi-br-plus-small" />}
                            onClick={handleAddMember}>
                            Ajouter un membre
                        </Button>
                    </FeatureCreditsDisabler>
                )}
            </HeaderSection>

            <OrganizationUserFilter
                totalDocs={meta.totalDocs}
                query={matcher.query}
                onQueryChange={handleQueryChange}
                sort={matcher.sort}
                onSortChange={handleSortChange}
                filter={matcher.filter}
                onFilterChange={handleFilterChange}
                viewType={viewType}
                onViewTypeChange={setViewType}
            />

            {viewType === 'list' ? (
                <OrganizationUsersDataTable
                    users={organizationUsers ?? []}
                    totalDocs={meta.totalDocs}
                    loading={fetchLoading}
                    isActionLoading={isActionLoading}
                    page={tablePage}
                    rowsPerPage={tablePageSize}
                    selectionModel={tableSelectionModel}
                    onPageChange={(page) => setTablePage(page)}
                    onPageSizeChange={(size) => { setTablePageSize(size); setTablePage(1); }}
                    onSelectionModelChange={setTableSelectionModel}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onResendInvitation={handleResendInvitation}
                    onUpdateStatus={handleUpdateStatus}
                />
            ) : (
                <OrganizationUsersDataGrid
                    proId={pro._id}
                    users={organizationUsers ?? []}
                    loading={fetchLoading}
                    isActionLoading={isActionLoading}
                    meta={meta}
                    pagination={matcher.pagination}
                    onPaginationChange={handleGridPaginationChange}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onResendInvitation={handleResendInvitation}
                    onUpdateStatus={handleUpdateStatus}
                />
            )}
        </Stack>
    );
};

// ─── Styled Components ────────────────────────────────────────────────────────

const HeaderSection = styled(Stack)(({ theme }) => ({
    alignItems: 'flex-start',
    marginBottom: theme.spacing(3.5),
    justifyContent: 'space-between',
    flexDirection: 'column',
    gap: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
        alignItems: 'center',
        flexDirection: 'row',
    },
}));

const HeaderInfoGroup = styled(Stack)(() => ({
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: 8,
}));

const Title = styled(Stack)(({ theme }) => ({
    color: theme.palette.text.primary,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '28px',
    fontWeight: 700,
}));

const Subtitle = styled(Stack)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontFamily: 'Plus Jakarta Sans',
    fontSize: '16px',
    fontWeight: 400,
}));

const AlertTypography = styled('p')(() => ({
    margin: 0,
    lineHeight: 1.2,
}));

export default OrganizationUserList;
